import { useCallback, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

/**
 * Lets a page ask "Delete X?" from any row action with one call, the way
 * `window.confirm()` used to be called — but rendered as our ConfirmDialog.
 *
 *   const { confirm, dialog } = useConfirmDialog();
 *   ...
 *   confirm({
 *     title: `Delete ${record.companyName}?`,
 *     description: "This company will be permanently deleted. This action cannot be undone.",
 *     onConfirm: () => deleteCompany({ variables: { companyId: record._id } }),
 *   });
 *   ...
 *   return <>{table}{dialog}</>;
 *
 * If `onConfirm` returns a promise the Delete button shows a spinner until it
 * settles. The dialog always closes afterwards — success and failure alike —
 * because the page already reports both through its mutation's toasts.
 */
const useConfirmDialog = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Kept after close so the title does not blank out during the fade-out.
  const [request, setRequest] = useState(null);

  const confirm = useCallback((options) => {
    setRequest(options);
    setLoading(false);
    setOpen(true);
  }, []);

  const close = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!request) return;
    setLoading(true);
    try {
      await request.onConfirm();
    } catch (e) {
      // Error reporting belongs to the caller's mutation handlers.
    } finally {
      close();
    }
  };

  const dialog = (
    <ConfirmDialog
      open={open}
      title={request?.title}
      description={request?.description}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      danger={request?.danger ?? true}
      icon={request?.icon}
      loading={loading}
      onConfirm={handleConfirm}
      onCancel={close}
    />
  );

  return { confirm, dialog };
};

export default useConfirmDialog;
